import { Suspense, lazy } from 'react'
import { Routes, Route, Navigate, Outlet } from 'react-router-dom'
import { Toaster } from '@/shared/components/ui/toaster'
import { ThemeProvider } from '@/shared/components/theme/theme-provider'
import { AuthProvider, useAuthContext } from '@/contexts/AuthContext'
// Features
import { LandingPage } from '@/features/landing/LandingPage'
import { AdminLogin } from '@/features/admin/login/AdminLogin'
import { DiscordCallback } from '@/features/admin/login/DiscordCallback'
import AdminDashboard from '@/features/admin/dashboard/page'
import ServersPage from '@/features/admin/dashboard/pages/servers/page'
import UsersPage from '@/features/admin/dashboard/pages/users/page'
import MonitoringPage from '@/features/admin/dashboard/pages/monitoring/page'
import ApiPage from '@/features/admin/dashboard/pages/api/page'
import DatabasePage from '@/features/admin/dashboard/pages/database/page'
import TerminalPage from '@/features/admin/dashboard/pages/terminal/page'
import SettingsPage from '@/features/admin/dashboard/pages/settings/page'

const BotPage = lazy(() => import('@/features/admin/dashboard/pages/bot/page'))
const ZomboidPage = lazy(() => import('@/features/admin/dashboard/pages/zomboid/page'))

// Protected Route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthContext()
  
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }
  
  return <>{children}</>
}

function AdminOutlet() {
  return <Outlet />
}

function App() {
  return (
    <AuthProvider>
      <ThemeProvider defaultTheme="dark" storageKey="ummr-ui-theme">
        <div className="min-h-screen bg-background font-sans antialiased">
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<AdminLogin />} />
            <Route path="/auth/discord/callback" element={<DiscordCallback />} />

            {/* Protected Routes */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminOutlet />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="servers" element={<ServersPage />} />
              <Route path="users" element={<UsersPage />} />
              <Route
                path="bot"
                element={
                  <Suspense
                    fallback={
                      <div className="min-h-[240px] rounded border bg-card/60 p-6 text-sm text-muted-foreground">
                        Caricamento sezione bot...
                      </div>
                    }
                  >
                    <BotPage />
                  </Suspense>
                }
              />
              <Route path="monitoring" element={<MonitoringPage />} />
              <Route path="database" element={<DatabasePage />} />
              <Route path="terminal" element={<TerminalPage />} />
              <Route path="api" element={<ApiPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route
                path="zomboid"
                element={
                  <Suspense
                    fallback={
                      <div className="min-h-[240px] rounded border bg-card/60 p-6 text-sm text-muted-foreground">
                        Caricamento sezione zomboid...
                      </div>
                    }
                  >
                    <ZomboidPage />
                  </Suspense>
                }
              />
              <Route path="*" element={<Navigate to="dashboard" replace />} />
            </Route>
          </Routes>
          <Toaster />
        </div>
      </ThemeProvider>
    </AuthProvider>
  )
}

export default App
